import type { NavigateOptions } from "@tanstack/react-router";

import * as Sentry from "@sentry/react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createRouter, RouterProvider } from "@tanstack/react-router";
import posthog from "posthog-js";
import { PostHogProvider } from "posthog-js/react";
import { useEffect } from "react";
import { RouterProvider as AriaRouterProvider } from "react-aria-components";

import { routeTree } from "@/app/routeTree.gen";
import { FullPageLoader } from "@/components/fullpage-loader";
import { GlobalToastRegion } from "@/components/toast/toast-region";
import { env } from "@/config/env";
import { useVersionCheck } from "@/hooks/useVersionCheck";
import { initIntercom } from "@/lib/intercom";
import { WhitelabelProvider } from "@/providers/whitelabel-provider";

const queryClient = new QueryClient();

// Set up a Router instance
const router = createRouter({
  context: {
    queryClient,
  },
  defaultPendingComponent: () => <FullPageLoader />,
  defaultPreload: "intent",
  // This will ensure that the loader is always called when the route is preloaded or visited
  defaultPreloadStaleTime: 0,
  // Since we're using React Query, we don't want loader calls to ever be stale
  routeTree,
  scrollRestoration: true,
});

const EXTERNAL_ROUTES = new Set(["/my-sites", "/pricing", "/profile/billing"]);

// Paths served outside the SPA (backend endpoints, file downloads) must
// bypass client-side routing or the router would render a not-found page.
function isSpaHref(href: string): boolean {
  const path = href.split("?")[0] ?? href;

  return (
    href.startsWith("/") &&
    !href.startsWith("/api/") &&
    !EXTERNAL_ROUTES.has(path)
  );
}

// Register things for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

declare module "react-aria-components" {
  interface RouterConfig {
    routerOptions: Omit<NavigateOptions, "from" | "to">;
  }
}

Sentry.init({
  dsn: env.VITE_SENTRY_DSN,
  environment: env.VITE_SENTRY_ENVIRONMENT,
  integrations: [Sentry.tanstackRouterBrowserTracingIntegration(router)],
  // Capture 100% of sessions where errors occur
  replaysOnErrorSampleRate: 1,
  // Capture 10% of all sessions for performance monitoring
  replaysSessionSampleRate: 0.1,
});

posthog.init(env.VITE_PUBLIC_POSTHOG_KEY, {
  api_host: env.VITE_PUBLIC_POSTHOG_HOST,
  capture_pageview: false,
  disable_session_recording: true,
  person_profiles: "identified_only",
  ui_host: "https://us.posthog.com",
});

export function App() {
  return <AppRouter />;
}

function AppRouter() {
  useVersionCheck();

  useEffect(() => {
    initIntercom();
  }, []);

  return (
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <WhitelabelProvider>
          <AriaRouterProvider
            navigate={(href, options) => {
              if (isSpaHref(href)) {
                router.navigate({ href, ...options });
              } else {
                window.location.assign(href);
              }
            }}
            useHref={(href) => href}
          >
            <RouterProvider router={router} />
          </AriaRouterProvider>
        </WhitelabelProvider>
        <ReactQueryDevtools buttonPosition="bottom-right" />
        <GlobalToastRegion aria-label="notification" />
      </QueryClientProvider>
    </PostHogProvider>
  );
}
